import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiPost, isInvalidSessionError } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import BaseButton from "../../components/BaseButton/BaseButton";
import LinkButton from "../../components/BaseButton/LinkButton";
import DeviceItem from "../../components/DeviceItem";
import { useNavigationHeader } from "../../components/MainLayout";
import PageHeader from "../../components/PageHeader";
import { useAppData } from "../../data/AppDataProvider";
import styles from "./Devices.module.css";

const TOTAL_SLOTS = 5;
const QR_CODE_BACKGROUND = "e3e3e3";

type AppearStyle = CSSProperties & {
    "--appear-delay": string;
};

type BaseButtonStyle = AppearStyle & {
    "--base-button-icon-color"?: string;
};

type RawDevice = {
    id?: string | number;
    uuid?: string | number;
    hwid?: string | number;
    deviceModel?: string;
    deviceName?: string;
    model?: string;
    name?: string;
    platform?: string;
    os?: string;
    operatingSystem?: string;
    osVersion?: string;
    version?: string;
    platformVersion?: string;
    createdAt?: string | number | Date;
    created_at?: string | number | Date;
    lastSeenAt?: string | number | Date;
    last_seen_at?: string | number | Date;
};

type NormalizedDevice = {
    id: string | number;
    deviceModel: string;
    platform: string;
    osVersion: string;
    createdAt: string | number | Date;
};

type DevicesData = {
    devices_tariff_limit: number;
    devices_info: RawDevice[];
};

type StatusState = {
    isLoading: boolean;
    error?: string;
};

type DevicesAppData = {
    devicesData: DevicesData;
    devicesStatus: StatusState;
    refreshDevicesData: () => Promise<unknown> | unknown;
    refreshInstructionData: () => Promise<unknown> | unknown;
};

type AuthData = {
    logout: () => void;
    token?: string;
    telegramInitData?: string;
};

type NavigationHeaderData = {
    shouldShowNavigation: boolean;
    setBottomPanel: (panel: {
        actions: ReactNode | null;
        backAction?: { label: string; onClick: () => void } | null;
        transition?: boolean;
    } | null) => void;
};

type RequestError = {
    status?: number;
};

type ChangeDevicesOption = {
    devices?: number | string;
    amount_rub?: number | string;
};

type ChangeDevicesResponse = {
    options?: ChangeDevicesOption[];
};

function normalizeDevice(device: RawDevice, index: number): NormalizedDevice {
    return {
        id: device.id ?? device.uuid ?? device.hwid ?? `device-${index}`,
        deviceModel: device.deviceModel ?? device.deviceName ?? device.model ?? device.name ?? "Неизвестное устройство",
        platform: device.platform ?? device.os ?? device.operatingSystem ?? "Unknown",
        osVersion: device.osVersion ?? device.version ?? device.platformVersion ?? "",
        createdAt: device.createdAt ?? device.created_at ?? device.lastSeenAt ?? device.last_seen_at ?? Date.now(),
    };
}

function classNames(...classes: Array<string | false | null | undefined>): string {
    return classes.filter(Boolean).join(" ");
}

function getAppearStyle(): AppearStyle {
    return {
        "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s`,
    };
}

function getShareActionStyle(): BaseButtonStyle {
    return {
        ...getAppearStyle(),
        "--base-button-icon-color": "var(--color-accent)",
    };
}

function hasChangeDevicesOptions(response: ChangeDevicesResponse): boolean {
    return Array.isArray(response.options) && response.options.some((option) => (
        Number(option.devices) > 0 && Number(option.amount_rub) > 0
    ));
}

async function copyText(value: string): Promise<void> {
    if (!value) {
        return;
    }

    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
}

export default function Devices() {
    const auth = useAuth() as AuthData;
    const navigate = useNavigate();
    const { devicesData, devicesStatus, refreshDevicesData, refreshInstructionData } = useAppData() as DevicesAppData;
    const {
        shouldShowNavigation,
        setBottomPanel,
    } = useNavigationHeader() as NavigationHeaderData;

    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    const [isSharePanelOpen, setIsSharePanelOpen] = useState(false);
    const [clearStatus, setClearStatus] = useState<StatusState>({ isLoading: false, error: "" });
    const [isOpeningTariffUpgrade, setIsOpeningTariffUpgrade] = useState(false);
    const [shareState, setShareState] = useState({
        isLoading: false,
        error: "",
        token: "",
        copied: false,
    });
    const previousSharePanelOpenRef = useRef(isSharePanelOpen);
    const { isLoading, error } = devicesStatus;

    const maxDevices = Math.max(0, Math.min(TOTAL_SLOTS, devicesData.devices_tariff_limit || 0));
    const devices = devicesData.devices_info.map(normalizeDevice).slice(0, maxDevices);
    const availableSlotsCount = Math.max(0, maxDevices - devices.length);
    const lockedSlotsCount = Math.max(0, TOTAL_SLOTS - maxDevices);
    const availableRenderCount = isLoading && !devices.length ? TOTAL_SLOTS : availableSlotsCount;
    const lockedRenderCount = isLoading ? 0 : lockedSlotsCount;
    const shareUrl = shareState.token && typeof window !== "undefined"
        ? `${window.location.origin}/instruction/shared/${encodeURIComponent(shareState.token)}`
        : "";
    const qrCodeUrl = shareUrl
        ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&bgcolor=${QR_CODE_BACKGROUND}&data=${encodeURIComponent(shareUrl)}`
        : "";

    useEffect(() => {
        void refreshDevicesData();
    }, [refreshDevicesData]);

    const confirmClearDevices = async () => {
        setClearStatus({ isLoading: true, error: "" });

        try {
            await apiPost("/cabinet/revoke_link", {}, auth);
            await Promise.all([
                refreshDevicesData(),
                refreshInstructionData(),
            ]);
            setIsClearModalOpen(false);
            setClearStatus({ isLoading: false, error: "" });
        } catch (error) {
            if (isInvalidSessionError(error as RequestError)) {
                auth.logout();
                return;
            }

            setClearStatus({
                isLoading: false,
                error: "Не удалось очистить устройства",
            });
        }
    };

    const openClearModal = useCallback(() => {
        setClearStatus({ isLoading: false, error: "" });
        setIsClearModalOpen(true);
    }, []);

    const openTariffUpgrade = useCallback(async () => {
        if (isOpeningTariffUpgrade) {
            return;
        }

        setIsOpeningTariffUpgrade(true);

        try {
            const response = await apiPost("/cabinet/change_devices_page_data", {}, auth) as ChangeDevicesResponse;
            navigate(hasChangeDevicesOptions(response) ? "/recalc" : "/tariffs");
        } catch (error) {
            if (isInvalidSessionError(error as RequestError)) {
                auth.logout();
                return;
            }

            navigate("/tariffs");
        } finally {
            setIsOpeningTariffUpgrade(false);
        }
    }, [auth, isOpeningTariffUpgrade, navigate]);

    const openSharePanel = useCallback(async () => {
        setIsSharePanelOpen(true);
        setShareState({
            isLoading: true,
            error: "",
            token: "",
            copied: false,
        });

        try {
            const response = await apiPost("/cabinet/create_share_link", {}, auth) as { token?: string };
            setShareState({
                isLoading: false,
                error: "",
                token: response.token || "",
                copied: false,
            });
        } catch (error) {
            if (isInvalidSessionError(error as RequestError)) {
                auth.logout();
                return;
            }

            setShareState({
                isLoading: false,
                error: "Не удалось создать ссылку",
                token: "",
                copied: false,
            });
        }
    }, [auth]);

    const closeSharePanel = useCallback(() => {
        setIsSharePanelOpen(false);
    }, []);

    const handleCopyShareLink = useCallback(async () => {
        if (!shareUrl) {
            return;
        }

        try {
            await copyText(shareUrl);
            setShareState((current) => ({
                ...current,
                copied: true,
                error: "",
            }));
        } catch {
            setShareState((current) => ({
                ...current,
                error: "Не удалось скопировать ссылку",
            }));
        }
    }, [shareUrl]);

    const shareAction = useMemo(() => (
        <BaseButton
            className={`w100 ${shouldShowNavigation ? "" : "menu-appear"} ${styles.shareAction}`}
            icon="qr_code.svg"
            onClick={() => {
                void openSharePanel();
            }}
            style={getShareActionStyle()}
            type="button"
        >
            <span>Поделиться VPN доступом</span>
        </BaseButton>
    ), [openSharePanel, shouldShowNavigation]);

    const sharePanel = useMemo(() => (
        <div className={`flex-col gap05 ${styles.sharePanel}`}>
            <div className={styles.sharePanelHeader}>
                <span className="font11">Одноразовая ссылка</span>
            </div>

            <p className={`font08 grey ${styles.shareDescription}`}>
                <span>Отправьте эту ссылку на другое устройство или сканируйте QR-код обычной камерой смартфона (не в приложении incy).</span>
            </p>

            {shareState.error ? (
                <div className={`default_block default_block_padded ${styles.errorBlock}`}>
                    <span>{shareState.error}</span>
                </div>
            ) : null}

            <div className={`flex-center w100 ${styles.qrWrap}`}>
                <div className={`flex-center ${styles.qrCode}`}>
                    {shareState.isLoading ? (
                        <span className="font08 grey">Загрузка...</span>
                    ) : qrCodeUrl ? (
                        <img
                            className={styles.qrImage}
                            src={qrCodeUrl}
                            alt="QR code"
                        />
                    ) : (
                        <span className="font08 grey">QR-код появится здесь</span>
                    )}
                </div>
            </div>

            <div className={styles.copyBlock}>
                <button
                    type="button"
                    className={styles.copyButton}
                    onClick={() => {
                        void handleCopyShareLink();
                    }}
                    disabled={shareState.isLoading || !shareUrl}
                    aria-label="Скопировать ссылку"
                >
                    <span className={`font09 ${styles.copyText}`}>
                        {shareState.isLoading ? "Загрузка..." : (shareUrl || "Ссылка появится здесь")}
                    </span>
                    <span className={styles.copyIcon} aria-hidden="true">
                        <svg className="svg-btns">
                            <use href="#icon-rect"></use>
                        </svg>
                    </span>
                </button>
                <div className="flex align-center grey">
                    <span className={styles.copyHint}>
                        {shareState.copied ? "Ссылка скопирована" : "Нажмите, чтобы скопировать"}
                    </span>
                </div>
            </div>
        </div>
    ), [handleCopyShareLink, qrCodeUrl, shareState.copied, shareState.error, shareState.isLoading, shareUrl]);

    useEffect(() => {
        if (!shouldShowNavigation) {
            setBottomPanel(null);
            return undefined;
        }

        const shouldTransition = previousSharePanelOpenRef.current !== isSharePanelOpen;
        previousSharePanelOpenRef.current = isSharePanelOpen;

        setBottomPanel({
            actions: isSharePanelOpen ? sharePanel : shareAction,
            backAction: isSharePanelOpen ? { label: "Закрыть", onClick: closeSharePanel } : null,
            transition: shouldTransition,
        });
    }, [
        closeSharePanel,
        isSharePanelOpen,
        setBottomPanel,
        shareAction,
        sharePanel,
        shouldShowNavigation,
    ]);

    useEffect(() => {
        return () => {
            setBottomPanel(null);
        };
    }, [setBottomPanel]);

    return (
        <>
            <div className={styles.header}>
                <PageHeader title="Устройства" className={styles.pageHeader} />
                <div className={styles.headerMeta}>
                    <span className={styles.headerSummary}>
                        Подключено <b>{isLoading ? "..." : devices.length}</b> из <b>{isLoading ? "..." : maxDevices}</b>
                    </span>
                    <button
                        className={styles.resetDevicesButton}
                        onClick={openClearModal}
                        type="button"
                    >
                        <span
                            className={styles.resetDevicesIcon}
                            style={{
                                WebkitMask: 'url("/svg/trash.svg") center / contain no-repeat',
                                mask: 'url("/svg/trash.svg") center / contain no-repeat',
                            }}
                            aria-hidden="true"
                        />
                        <span>Сбросить устройства</span>
                    </button>
                </div>
            </div>

            <div className={`flex-col gap1 ${styles.devicesList}`}>
                {error ? (
                    <div className={`default_block default_block_padded menu-appear ${styles.errorBlock}`} style={getAppearStyle()}>
                        <span>{error}</span>
                    </div>
                ) : null}

                {devices.map((device) => (
                    <div key={`device-${device.id}`} className="menu-appear" style={getAppearStyle()}>
                        <DeviceItem device={device} />
                    </div>
                ))}

                <div className={`flex-col gap05 ${styles.slotSection}`}>
                    <h3 className={styles.sectionTitle}>Доступные слоты</h3>
                    {Array.from({ length: availableRenderCount }).map((_, index) => (
                        isLoading && !devices.length ? (
                            <BaseButton
                                key={`loading-${index}`}
                                icon="icon-plus"
                                disabled={true}
                                className={`menu-appear ${styles.availableSlotButton}`}
                                style={getAppearStyle()}
                            >
                                <span>Загрузка...</span>
                            </BaseButton>
                        ) : (
                            <LinkButton
                                key={`connect-${index}`}
                                icon="icon-plus"
                                to="/instruction"
                                className={`menu-appear ${styles.availableSlotButton}`}
                                style={getAppearStyle()}
                            >
                                <span className={styles.slotButtonTitle}>Подключить устройство</span>
                            </LinkButton>
                        )
                    ))}
                </div>

                <div className={`flex-col gap05 ${styles.slotSection}`}>
                    <h3 className={styles.sectionTitle}>Заблокированные слоты</h3>
                    {Array.from({ length: lockedRenderCount }).map((_, index) => (
                        <BaseButton
                            key={`locked-${index}`}
                            icon="lock.svg"
                            disabled={isOpeningTariffUpgrade}
                            onClick={() => {
                                void openTariffUpgrade();
                            }}
                            className={`menu-appear ${styles.lockedSlotButton}`}
                            style={getAppearStyle()}
                            type="button"
                        >
                            <span className={styles.lockedSlotContent}>
                                <span className={styles.lockedSlotTitle}>Недоступно</span>
                                <span className={styles.lockedSlotDescription}>
                                    Расширьте тариф, чтобы добавить устройство
                                </span>
                            </span>
                        </BaseButton>
                    ))}
                </div>
            </div>

            {!shouldShowNavigation ? (
                isSharePanelOpen ? (
                    <div className="flex-col gap05">
                        {sharePanel}
                        <BaseButton
                            type="button"
                            onClick={closeSharePanel}
                            contentAlign="center"
                            showArrow={false}
                        >
                            <span>Закрыть</span>
                        </BaseButton>
                    </div>
                ) : shareAction
            ) : null}

            <div className={classNames(styles.modalOverlay, isClearModalOpen && styles.modalOverlayActive)}>
                <div className={`flex-col gap05 pad1 ${styles.modalWindow}`}>
                    <h2 className={`font11 ${styles.modalTitle}`}>
                        Очистить устройства?
                    </h2>
                    <p className="font08">
                        Все устройства удалятся из списка подключенных. Вы сможете подключить их снова.
                    </p>
                    {clearStatus.error ? <p className="font08">{clearStatus.error}</p> : null}

                    <div className={styles.modalActions}>
                        <button
                            className={`w100 ${styles.modalButton} ${styles.dangerModalButton} ${styles.modalAction}`}
                            onClick={confirmClearDevices}
                            disabled={clearStatus.isLoading}
                            type="button"
                        >
                            {clearStatus.isLoading ? "Очистка..." : "Очистить"}
                        </button>
                        <button
                            className={`w100 ${styles.modalButton} ${styles.modalAction}`}
                            onClick={() => setIsClearModalOpen(false)}
                            disabled={clearStatus.isLoading}
                            type="button"
                        >
                            <span>Отмена</span>
                        </button>
                    </div>
                </div>
            </div>

        </>
    );
}
